import { api } from './api'
import { i18n } from '@/i18n'
import { createUploadError } from '@/utils/uploadErrors'

/**
 * Normaliza el cuerpo de GET /media a un array de registros.
 * @param {unknown} body
 * @returns {object[]}
 */
function normalizeMediaListPayload(body) {
  if (Array.isArray(body)) return body
  const candidate = body?.data ?? body
  if (Array.isArray(candidate)) return candidate
  if (candidate && typeof candidate === 'object') {
    const inner = candidate.items ?? candidate.media ?? candidate.files ?? candidate.records
    if (Array.isArray(inner)) return inner
  }
  return []
}

/**
 * Servicio para gestión de multimedia con S3 via presigned URLs.
 * Flujo: solicitar URL → subir a S3 → confirmar subida
 */
export const mediaService = {
  async listFolders(clientId) {
    const response = await api.get(`/media/folders?client_id=${encodeURIComponent(clientId)}`, true)
    return normalizeMediaListPayload(response)
  },

  async createFolder({ client_id, name, parent_id = null, color }) {
    const response = await api.post('/media/folders', { client_id, name, parent_id, color }, true)
    return response?.data ?? response
  },

  async updateFolder(folderUuid, changes) {
    const response = await api.patch(`/media/folders/${folderUuid}`, changes, true)
    return response?.data ?? response
  },

  async deleteFolder(folderUuid) {
    return api.delete(`/media/folders/${folderUuid}`, true)
  },

  async moveFile(mediaUuid, folderId = null) {
    const response = await api.patch(`/media/${mediaUuid}/move`, { folder_id: folderId }, true)
    return response?.data ?? response
  },

  /**
   * Lista archivos (p. ej. expediente por cliente).
   * GET /media?company_id=&client_id=&entity_type=&worker_id=
   * @param {{ company_id?, client_id?, entity_type?, worker_id? }} [filters]
   * @returns {Promise<object[]>}
   */
  async list(filters = {}) {
    const params = new URLSearchParams()
    if (filters.company_id != null && filters.company_id !== '') {
      params.set('company_id', String(filters.company_id))
    }
    if (filters.client_id != null && filters.client_id !== '') {
      params.set('client_id', String(filters.client_id))
    }
    if (filters.entity_type) {
      params.set('entity_type', String(filters.entity_type))
    }
    if (filters.worker_id != null && filters.worker_id !== '') {
      params.set('worker_id', String(filters.worker_id))
    }
    const qs = params.toString()
    const path = qs ? `/media?${qs}` : '/media'
    const body = await api.get(path, true)
    return normalizeMediaListPayload(body)
  },

  /**
   * Paso 1: Solicita una presigned URL para subir un archivo.
   * El backend valida espacio disponible del plan y reserva size_bytes.
   * @param {File} file - Archivo a subir
   * @param {Object} options - { entity_type, company_id, worker_id, client_id, folder_id }
   * @returns {{ uploadUrl, media_uuid, s3_key, expires_in, storage }}
   */
  async requestUploadUrl(file, { entity_type = 'general', company_id, worker_id, client_id, folder_id } = {}) {
    const body = {
      fileName: file.name,
      contentType: file.type || 'application/octet-stream',
      size_bytes: file.size,
      entity_type,
      ...(company_id && { company_id }),
      ...(worker_id && { worker_id }),
      ...(client_id != null && client_id !== '' && { client_id: Number(client_id) || client_id }),
      ...(folder_id != null && folder_id !== '' && { folder_id })
    }
    const response = await api.post('/media/upload-url', body, true)
    const data = response?.data ?? response ?? {}
    return {
      ...data,
      uploadUrl: data.upload_url ?? data.uploadUrl,
      media_uuid: data.media_uuid ?? data.uuid
    }
  },

  /**
   * Paso 2: Sube el archivo directamente a S3 usando la presigned URL
   * @param {string} uploadUrl - URL presignada de S3
   * @param {File} file - Archivo a subir
   */
  async uploadToS3(uploadUrl, file) {
    const contentType = file.type || 'application/octet-stream'
    let url = uploadUrl
    if (import.meta.env.DEV) {
      try {
        const parsed = new URL(uploadUrl)
        if (parsed.hostname.includes('.s3.') && parsed.hostname.endsWith('.amazonaws.com')) {
          url = `/s3-upload/${parsed.hostname}${parsed.pathname}${parsed.search}`
        }
      } catch { /* keep original URL */ }
    }
    const t = (key, params) => i18n.global.t(key, params)
    let response
    try {
      response = await fetch(url, {
        method: 'PUT',
        headers: { 'Content-Type': contentType },
        body: file
      })
    } catch (error) {
      console.error('[MediaService] uploadToS3 network:', error)
      throw createUploadError({ stage: 'network', status: null }, t)
    }
    if (!response.ok) {
      const body = await response.text().catch(() => '')
      console.error('[MediaService] uploadToS3', response.status, body)
      throw createUploadError({ stage: 's3', status: response.status, body }, t)
    }
  },

  /**
   * Paso 3: Confirma la subida para que el registro quede activo en BD
   * @param {string} media_uuid
   * @param {number} file_size - Tamaño en bytes (opcional pero recomendado)
   */
  async confirmUpload(media_uuid, file_size) {
    return api.post(`/media/${media_uuid}/confirm`, { file_size }, true)
  },

  /**
   * Notifica al backend que el upload falló para liberar los bytes reservados.
   * Best-effort: nunca lanza, el error original del upload es el que importa.
   * @param {string} media_uuid
   */
  async markUploadFailed(media_uuid) {
    if (!media_uuid) return
    try {
      await api.post(`/media/${media_uuid}/fail`, {}, true)
    } catch (error) {
      console.error('[MediaService] markUploadFailed:', error)
    }
  },

  /**
   * Flujo completo: solicitar URL → subir → confirmar.
   * Si S3 o la confirmación fallan, libera la reserva via /media/:uuid/fail.
   * @param {File} file
   * @param {Object} options - { entity_type, company_id, worker_id, client_id, folder_id }
   * @returns {string} media_uuid
   */
  async upload(file, options = {}) {
    const { uploadUrl, media_uuid } = await this.requestUploadUrl(file, options)
    try {
      await this.uploadToS3(uploadUrl, file)
      await this.confirmUpload(media_uuid, file.size)
    } catch (error) {
      await this.markUploadFailed(media_uuid)
      throw error
    }
    return media_uuid
  },

  /**
   * Obtiene una URL temporal de descarga (expira en 15 min)
   * @param {string} media_uuid
   * @returns {string} downloadUrl
   */
  async getDownloadUrl(media_uuid) {
    const response = await api.get(`/media/${media_uuid}/download-url`, true)
    const root = response?.data ?? response
    const url = root?.downloadUrl ?? root?.download_url
    if (typeof url !== 'string' || !url) {
      throw new Error('URL de descarga no disponible')
    }
    return url
  },

  /**
   * Elimina un archivo de S3 y lo marca como inactivo en BD
   * @param {string} media_uuid
   */
  async delete(media_uuid) {
    return api.delete(`/media/${media_uuid}`, true)
  }
}
